/**
 * ErvaPOS - Layout Component
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import useFamaBranding from '../hooks/useFamaBranding';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGlobalView } from '../context/GlobalViewContext';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  Truck,
  Stethoscope,
  FileText,
  Receipt,
  Clock,
  ClipboardList,
  Settings,
  Upload,
  LogOut,
  Menu,
  X,
  Tag,
  BarChart3,
  ChevronDown,
    ChevronRight,
  User,
  Bell,
  MessageCircle,
  Send,
  Move,
  Leaf,
  Ticket,
  FileBox,
  RotateCcw,
  PackageSearch,
  CalendarClock,
  CalendarCheck,
  PackageMinus,
  ShieldCheck,
  LockKeyhole,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import api, { internalChatAPI, communicationsSmsAPI } from '../services/api';
import WorkdayConfirmDialog from './WorkdayConfirmDialog';
import UserSecurityModal from './UserSecurityModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: "/communications-sms", icon: MessageCircle, label: "Comunicações SMS", roles: ["manager","admin"] },
  { path: '/pos', icon: ShoppingCart, label: 'POS', highlight: true },
  { path: '/customer-orders', icon: PackageSearch, label: 'Encomendas/Reservas' },
  { path: '/returns', icon: RotateCcw, label: 'Devoluções' },
  { path: '/products', icon: Package, label: 'Produtos' },
  { path: '/stock', icon: Tag, label: 'Stock/Lotes' },
  { path: '/stock-writeoffs', icon: PackageMinus, label: 'Saídas Internas' },
  { path: '/supplier-returns', icon: Truck, label: 'Devoluções Fornecedor' },
  { path: '/rotulos', icon: Package, label: 'Rótulos Elemensys' },
  { path: '/customers', icon: Users, label: 'Clientes' },
  { path: '/providers', icon: Stethoscope, label: 'Prestadores' },
  { path: '/vouchers', icon: Ticket, label: 'Vouchers' },
  { path: '/suppliers', icon: Truck, label: 'Fornecedores' },
  { path: '/sales-history', icon: Receipt, label: 'Histórico Vendas' },
  { path: '/invoices', icon: FileText, label: 'Faturas Compra' },
  { path: '/expenses', icon: Receipt, label: 'Despesas', globalOnly: true },
  { path: '/timesheets', icon: CalendarClock, label: 'Folhas Ponto' },
  { path: '/employees', icon: Clock, label: 'Funcionários', globalOnly: true },
  { path: '/inventory', icon: ClipboardList, label: 'Inventário' },
  { path: '/stock-variances', icon: PackageMinus, label: 'Variações de Stock', roles: ['manager','admin'], globalOnly: true },
  { path: '/reports', icon: BarChart3, label: 'Relatórios', globalOnly: true },
  { path: '/supplier-replenishment', icon: Truck, label: 'Compras e Stock', roles: ['staff', 'manager', 'admin'] },
  { path: '/day-close', icon: CalendarCheck, label: 'Fecho de Dia', globalOnly: true },
  { path: '/saft-import', icon: Upload, label: 'Importar SAF-T', globalOnly: true },
  { path: '/settings', icon: Settings, label: 'Configurações', globalOnly: true },
  { path: "/notificationsbooking", icon: Bell, label: "Agenda e Notificações", roles: ["manager","admin"] },

];

const navGroups = [
  {
    label: 'Operação',
    paths: ['/pos', '/customer-orders', '/returns', '/sales-history']
  },
  {
    label: 'Catálogo e Loja',
    paths: ['/products', '/stock', '/stock-writeoffs', '/supplier-returns', '/rotulos', '/vouchers', '/suppliers', '/inventory', '/stock-variances']
  },
  {
    label: 'Equipa e Serviço',
    paths: ['/providers', '/timesheets', '/employees']
  },
  {
    label: 'Gestão',
    paths: ['/reports', '/supplier-replenishment', '/day-close', '/invoices', '/expenses', '/saft-import', '/settings']
  },
];


export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
    const [openNavSection, setOpenNavSection] = useState(null);
  const [showUserSecurityModal, setShowUserSecurityModal] = useState(false);
  const [showGlobalViewModal, setShowGlobalViewModal] = useState(false);
  const [globalPin, setGlobalPin] = useState('');
  const [globalPinError, setGlobalPinError] = useState('');
  const [globalPinLoading, setGlobalPinLoading] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [chatThreads, setChatThreads] = useState([]);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [smsUnreadCount, setSmsUnreadCount] = useState(0);
  const [smsUnreadPreview, setSmsUnreadPreview] = useState([]);
  const [smsAlertLoading, setSmsAlertLoading] = useState(false);
  const [orderAlerts, setOrderAlerts] = useState([]);
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [selectedChatThread, setSelectedChatThread] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const chatBottomRef = useRef(null);
  const chatFloatingRef = useRef(null);
  const chatDragRef = useRef({ dragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const [chatFloatingPosition, setChatFloatingPosition] = useState(() => {
    try {
      const saved = window.localStorage.getItem('famapos_internal_chat_position');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top)) {
          return parsed;
        }
      }
    } catch (error) {
      // ignora posição guardada inválida
    }

    return { left: null, top: null };
  });
  const chatSoundEnabledRef = useRef(false);
  const lastUnreadCountRef = useRef(0);
  const chatSoundRepeatTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isGlobalView, canUseGlobalView, unlockGlobalView, lockGlobalView } = useGlobalView();
  const { brandName, logoUrl } = useFamaBranding();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Hide sidebar on POS page for full screen
  const isPOS = location.pathname === '/pos';
  const isStockMovel = location.pathname === '/stockmovel';
  const hideChromeLayout = isPOS || isStockMovel;

  
    const role = user?.role;

    function canAccess(path) {
      // sem user, não mostra menu
      if (!role) return false;

      const navItem = navItems.find((item) => item.path === path);
      if (navItem?.globalOnly && !isGlobalView) return false;

      // admin vê tudo
      if (role === 'admin') return true;

      // manager: tudo exceto configurações
      if (role === 'manager') {
        const blocked = ['/settings'];
        return !blocked.includes(path);
      }

      // staff: operação + gestão de loja, sem áreas sensíveis de fecho/configuração
      if (role === 'staff') {
        const allowed = [
          '/', '/pos',
          '/customers',
            '/customer-account',
          '/customer-orders',
          '/returns',
          '/vouchers',
          '/timesheets',
          '/products',
          '/stock',
          '/stock-writeoffs',
          '/supplier-returns',
          '/suppliers',
          '/supplier-replenishment',
          '/invoices',
          '/expenses',
          '/inventory',
          '/rotulos',
          '/sales-history',
          '/providers'
        ];
        return allowed.includes(path);
      }

      // operator: caixa
      if (role === 'operator') {
        const allowed = [
          '/', '/pos',
          '/customers',
            '/customer-account',
          '/customer-orders',
          '/returns',
          '/vouchers',
          '/timesheets'
        ];
        return allowed.includes(path);
      }

      return false;
    }

  const handleGlobalUnlock = async (event) => {
    event?.preventDefault();
    setGlobalPinLoading(true);
    setGlobalPinError('');
    try {
      await unlockGlobalView(globalPin);
      setGlobalPin('');
      setShowGlobalViewModal(false);
    } catch (error) {
      setGlobalPinError(error.response?.data?.detail || 'Não foi possível abrir a vista Global');
    } finally {
      setGlobalPinLoading(false);
    }
  };



  function getNavSectionForPath(pathname) {
    if (pathname === '/customers' || pathname === '/customer-account') return 'Clientes';

    const matchedGroup = navGroups.find((group) => group.paths.includes(pathname));
    return matchedGroup?.label || null;
  }

  useEffect(() => {
    const targetSection = getNavSectionForPath(location.pathname);
    if (targetSection) setOpenNavSection(targetSection);
  }, [location.pathname]);

  function toggleNavSection(sectionLabel) {
    setOpenNavSection((prev) => (prev === sectionLabel ? null : sectionLabel));
  }

  const enableChatSound = useCallback(() => {
    chatSoundEnabledRef.current = true;
  }, []);

  const playInternalChatAlertSound = useCallback(() => {
    if (!chatSoundEnabledRef.current) return;

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.08);

      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.22, audioContext.currentTime + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.22);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.24);

      setTimeout(() => {
        try {
          audioContext.close();
        } catch (error) {
          // ignora fecho do áudio
        }
      }, 350);
    } catch (error) {
      // Alguns browsers bloqueiam som até existir interação real do utilizador.
    }
  }, []);

  const stopInternalChatAlertRepeat = useCallback(() => {
    if (chatSoundRepeatTimerRef.current) {
      clearInterval(chatSoundRepeatTimerRef.current);
      chatSoundRepeatTimerRef.current = null;
    }
  }, []);

  const startInternalChatAlertRepeat = useCallback(() => {
    if (chatSoundRepeatTimerRef.current) return;

    chatSoundRepeatTimerRef.current = setInterval(() => {
      if (Number(lastUnreadCountRef.current || 0) > 0 && !showInternalChat) {
        playInternalChatAlertSound();
      } else {
        stopInternalChatAlertRepeat();
      }
    }, 30000);
  }, [showInternalChat, playInternalChatAlertSound, stopInternalChatAlertRepeat]);

  const refreshSmsAlerts = useCallback(async () => {
    if (!user) return;

    setSmsAlertLoading(true);
    try {
      const res = await communicationsSmsAPI.getAll({
        direction: 'inbound',
        unread_only: true,
        limit: 20,
      });

      const unread = Array.isArray(res?.data) ? res.data : [];
      setSmsUnreadPreview(unread.slice(0, 5));
      setSmsUnreadCount(unread.length);
    } catch (error) {
      console.error('Erro ao atualizar alertas SMS:', error);
    } finally {
      setSmsAlertLoading(false);
    }
  }, [user]);

  const refreshOrderAlerts = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/customer-orders/automation-alerts');
      setOrderAlerts(Array.isArray(res?.data?.orders) ? res.data.orders : []);
    } catch (error) {
      console.error('Erro ao atualizar alertas de encomendas:', error);
    }
  }, [user]);

  const refreshChatSummary = useCallback(async () => {
    if (!user) return;
    try {
      await internalChatAPI.ping().catch(() => null);
      const [usersRes, threadsRes, unreadRes] = await Promise.all([
        internalChatAPI.getUsers(),
        internalChatAPI.getThreads(),
        internalChatAPI.getUnreadCount(),
      ]);
      const nextUnreadCount = Number(unreadRes.data?.unread_count || 0);
      const previousUnreadCount = Number(lastUnreadCountRef.current || 0);

      setChatUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setChatThreads(Array.isArray(threadsRes.data) ? threadsRes.data : []);
      setChatUnreadCount(nextUnreadCount);

      if (nextUnreadCount > previousUnreadCount && !showInternalChat) {
        playInternalChatAlertSound();
      }

      lastUnreadCountRef.current = nextUnreadCount;

      if (nextUnreadCount > 0 && !showInternalChat) {
        startInternalChatAlertRepeat();
      } else {
        stopInternalChatAlertRepeat();
      }

      setChatError('');
    } catch (error) {
      console.error('Erro ao atualizar chat interno:', error);
    }
  }, [user, showInternalChat, playInternalChatAlertSound, startInternalChatAlertRepeat, stopInternalChatAlertRepeat]);

  const getChatMessageTime = useCallback((value) => {
    if (!value) return 0;

    const raw = String(value).trim();
    if (!raw) return 0;

    const candidates = [
      raw,
      raw.replace(' ', 'T'),
      raw.replace(' ', 'T').replace(/\+00$/, '+00:00'),
      raw.replace(' ', 'T').replace(/\+0000$/, '+00:00'),
    ];

    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
      candidates.push(raw.replace(' ', 'T') + 'Z');
    }

    for (const candidate of candidates) {
      const time = Date.parse(candidate);
      if (!Number.isNaN(time)) {
        return time;
      }
    }

    return 0;
  }, []);

  const sortChatMessages = useCallback((messages) => {
    const list = Array.isArray(messages) ? messages : [];

    return [...list].sort((a, b) => {
      const diff = getChatMessageTime(a?.created_at) - getChatMessageTime(b?.created_at);
      if (diff !== 0) return diff;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
  }, [getChatMessageTime]);

  const loadChatMessages = useCallback(async (threadId) => {
    if (!threadId) return;
    try {
      const res = await internalChatAPI.getMessages(threadId, 80);
      const incomingMessages = Array.isArray(res.data) ? res.data : [];

      setChatMessages((prev) => {
        const previousMessages = Array.isArray(prev) ? prev : [];
        const mergedById = new Map();

        previousMessages
          .filter((message) => String(message.thread_id || '') === String(threadId))
          .forEach((message) => {
            if (message?.id) {
              mergedById.set(String(message.id), message);
            }
          });

        incomingMessages.forEach((message) => {
          if (message?.id) {
            mergedById.set(String(message.id), message);
          }
        });

        return sortChatMessages(Array.from(mergedById.values())).slice(-120);
      });

      setChatError('');
      await refreshChatSummary();
    } catch (error) {
      console.error('Erro ao carregar conversa:', error);
      setChatError(error.response?.data?.detail || 'Não foi possível carregar a conversa.');
    }
  }, [refreshChatSummary, sortChatMessages]);

  useEffect(() => {
    const activateSound = () => {
      chatSoundEnabledRef.current = true;
    };

    window.addEventListener('click', activateSound, { once: true });
    window.addEventListener('keydown', activateSound, { once: true });
    window.addEventListener('touchstart', activateSound, { once: true });

    return () => {
      window.removeEventListener('click', activateSound);
      window.removeEventListener('keydown', activateSound);
      window.removeEventListener('touchstart', activateSound);
      stopInternalChatAlertRepeat();
    };
  }, [stopInternalChatAlertRepeat]);

  useEffect(() => {
    refreshChatSummary();
    const timer = setInterval(refreshChatSummary, 45000);
    return () => clearInterval(timer);
  }, [refreshChatSummary]);

  useEffect(() => {
    refreshSmsAlerts();
    refreshOrderAlerts();
    const timer = setInterval(refreshSmsAlerts, 45000);
    const orderTimer = setInterval(refreshOrderAlerts, 45000);
    const onOrderAlertsChanged = () => refreshOrderAlerts();
    window.addEventListener('customer-order-alerts-changed', onOrderAlertsChanged);
    return () => {
      clearInterval(timer);
      clearInterval(orderTimer);
      window.removeEventListener('customer-order-alerts-changed', onOrderAlertsChanged);
    };
  }, [refreshSmsAlerts, refreshOrderAlerts]);

  useEffect(() => {
    if (!showInternalChat || !selectedChatThread?.id) return;
    loadChatMessages(selectedChatThread.id);
    const timer = setInterval(() => loadChatMessages(selectedChatThread.id), 15000);
    return () => clearInterval(timer);
  }, [showInternalChat, selectedChatThread?.id, loadChatMessages]);

  useEffect(() => {
    if (showInternalChat && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showInternalChat, chatMessages.length]);

  const openChatWithUser = async (chatUser) => {
    setSelectedChatUser(chatUser);
    const existing = chatThreads.find((thread) => String(thread.other_user_id) === String(chatUser.id));
    setSelectedChatThread(existing || null);
    setChatMessages([]);
    setChatError('');

    if (existing?.id) {
      await loadChatMessages(existing.id);
    }
  };

  const sendChatMessage = async () => {
    const body = chatText.trim();
    if (!body || !selectedChatUser || chatLoading) return;

    setChatLoading(true);
    setChatError('');
    try {
      const sentRes = await internalChatAPI.sendMessage(selectedChatUser.id, body);
      const sentMessage = sentRes?.data || null;

      setChatText('');

      if (sentMessage?.id) {
        setChatMessages((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((item) => String(item.id) === String(sentMessage.id))) {
            return list;
          }
          return [...list, sentMessage];
        });
      }

      await refreshChatSummary();

      const refreshedThreads = await internalChatAPI.getThreads();
      const threads = Array.isArray(refreshedThreads.data) ? refreshedThreads.data : [];
      setChatThreads(threads);
      const thread = threads.find((item) => String(item.other_user_id) === String(selectedChatUser.id));
      if (thread?.id) {
        setSelectedChatThread(thread);
        await loadChatMessages(thread.id);
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setChatError(error.response?.data?.detail || 'Não foi possível enviar a mensagem.');
    } finally {
      setChatLoading(false);
    }
  };

  const formatChatTime = (value) => {
    if (!value) return '';
    const time = getChatMessageTime(value);

    if (!time) {
      return String(value).slice(0, 16);
    }

    try {
      return new Date(time).toLocaleString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return String(value).slice(0, 16);
    }
  };

  const chatThreadForUser = (chatUser) => {
    return chatThreads.find((thread) => String(thread.other_user_id) === String(chatUser.id));
  };

  const orderedChatUsers = [...chatUsers].sort((a, b) => {
    const ta = chatThreadForUser(a);
    const tb = chatThreadForUser(b);
    const da = ta?.last_message_at || ta?.updated_at || '';
    const db = tb?.last_message_at || tb?.updated_at || '';
    if (da && db && da !== db) return String(db).localeCompare(String(da));
    if (da && !db) return -1;
    if (!da && db) return 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });

  const resetChatFloatingPosition = useCallback(() => {
    const width = Math.min(920, Math.max(340, window.innerWidth - 32));
    const height = Math.min(680, Math.max(420, window.innerHeight - 32));
    const next = {
      left: Math.max(16, window.innerWidth - width - 24),
      top: Math.max(16, window.innerHeight - height - 24),
    };

    setChatFloatingPosition(next);
    try {
      window.localStorage.setItem('famapos_internal_chat_position', JSON.stringify(next));
    } catch (error) {
      // ignora falha de localStorage
    }
  }, []);

  const getChatFloatingStyle = () => {
    const width = 'min(920px, calc(100vw - 32px))';
    const height = 'min(680px, calc(100vh - 32px))';

    if (Number.isFinite(chatFloatingPosition.left) && Number.isFinite(chatFloatingPosition.top)) {
      return {
        left: `${chatFloatingPosition.left}px`,
        top: `${chatFloatingPosition.top}px`,
        width,
        height,
      };
    }

    return {
      right: '24px',
      bottom: '24px',
      width,
      height,
    };
  };

  const startChatDrag = (event) => {
    if (!chatFloatingRef.current) return;

    const point = event.touches?.[0] || event;
    const rect = chatFloatingRef.current.getBoundingClientRect();

    chatDragRef.current = {
      dragging: true,
      startX: point.clientX,
      startY: point.clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };

    event.preventDefault();
  };

  useEffect(() => {
    const handleMove = (event) => {
      if (!chatDragRef.current.dragging) return;

      const point = event.touches?.[0] || event;
      const width = chatFloatingRef.current?.offsetWidth || 920;
      const height = chatFloatingRef.current?.offsetHeight || 680;

      const nextLeft = chatDragRef.current.startLeft + point.clientX - chatDragRef.current.startX;
      const nextTop = chatDragRef.current.startTop + point.clientY - chatDragRef.current.startY;

      const bounded = {
        left: Math.min(Math.max(8, nextLeft), Math.max(8, window.innerWidth - width - 8)),
        top: Math.min(Math.max(8, nextTop), Math.max(8, window.innerHeight - height - 8)),
      };

      setChatFloatingPosition(bounded);
    };

    const handleUp = () => {
      if (!chatDragRef.current.dragging) return;

      chatDragRef.current.dragging = false;

      try {
        const rect = chatFloatingRef.current?.getBoundingClientRect();
        if (rect) {
          window.localStorage.setItem('famapos_internal_chat_position', JSON.stringify({
            left: Math.round(rect.left),
            top: Math.round(rect.top),
          }));
        }
      } catch (error) {
        // ignora falha de localStorage
      }
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, []);

  return (
    <div className="app-layout">
      {/* Sidebar */}
      {!hideChromeLayout && (
        <aside className={`sidebar ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
          {/* Logo */}
          <div className="p-4 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={brandName}
                  className="h-12 w-12 rounded-xl object-contain bg-white p-1.5 shadow-sm"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Leaf className="w-7 h-7 text-emerald-400" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white font-['Manrope']">{brandName}</h1>
                <p className="text-xs text-slate-500">Espaço Saúde</p>
              </div>
            </Link>
          </div>

            {/* Navigation */}
            <nav className="flex-1 p-3 overflow-y-auto scrollbar-dark">
              <ul className="space-y-3">
                {canAccess('/pos') && (
                  <li>
                    <Link
                      to="/pos"
                      data-testid="nav-open-pos-top"
                      className={`sidebar-item justify-center bg-emerald-500 font-semibold text-slate-950 shadow-sm hover:bg-emerald-400 hover:text-slate-950 ${location.pathname === '/pos' ? 'ring-2 ring-emerald-300 ring-offset-2 ring-offset-slate-950' : ''}`}
                    >
                      <ShoppingCart className="w-5 h-5" />
                      <span>Abrir POS</span>
                    </Link>
                  </li>
                )}

                {(() => {
                  const item = navItems.find((entry) => entry.path === '/');
                  if (!item || !canAccess(item.path)) return null;
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <li>
                      <Link
                        to={item.path}
                        data-testid="nav-dashboard"
                        onClick={(event) => {
                          if (location.pathname === '/') {
                            event.preventDefault();
                            window.location.reload();
                          }
                        }}
                        className={`sidebar-item ${isActive ? 'active' : ''}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })()}

                {(() => {
                  const item = navItems.find((entry) => entry.path === '/communications-sms');
                  if (!item || !canAccess(item.path)) return null;
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <li>
                      <Link
                        to={item.path}
                        data-testid="nav-communications-sms-direct"
                        className={`sidebar-item ${isActive ? 'active' : ''}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })()}

                {navGroups.map((group) => {
                  const items = group.paths
                    .map((groupPath) => navItems.find((entry) => entry.path === groupPath))
                    .filter((entry) => entry && canAccess(entry.path));

                  if (!items.length) return null;

                  const isGroupOpen = openNavSection === group.label;
                  const hasActiveChild = items.some((item) => location.pathname === item.path);

                  return (
                    <li key={group.label}>
                      <button
                        type="button"
                        onClick={() => toggleNavSection(group.label)}
                        className={`sidebar-item w-full flex items-center justify-between ${isGroupOpen || hasActiveChild ? 'active' : ''}`}
                        data-testid={`nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        <span>{group.label}</span>
                        {isGroupOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>

                      {isGroupOpen && (
                        <ul className="mt-1 ml-4 space-y-1">
                          {items.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;

                            return (
                              <li key={item.path}>
                                <Link
                                  to={item.path}
                                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                                  className={`sidebar-item ${isActive ? 'active' : ''} ${item.highlight ? 'bg-emerald-500/10 hover:bg-emerald-500/20' : ''}`}
                                >
                                  <Icon className={`w-5 h-5 ${item.highlight && !isActive ? 'text-emerald-400' : ''}`} />
                                  <span className={item.highlight && !isActive ? 'text-emerald-400 font-medium' : ''}>
                                    {item.label}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}

                {canAccess('/customers') && (
                  <li>
                    <button
                      type="button"
                      onClick={() => toggleNavSection('Clientes')}
                      className={`sidebar-item w-full flex items-center justify-between ${openNavSection === 'Clientes' || location.pathname === '/customers' || location.pathname === '/customer-account' ? 'active' : ''}`}
                      data-testid="nav-customers-group"
                    >
                      <span className="flex items-center gap-3">
                        <Users className="w-5 h-5" />
                        <span>Clientes</span>
                      </span>
                      {openNavSection === 'Clientes' ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>

                    {openNavSection === 'Clientes' && (
                      <ul className="mt-1 ml-4 space-y-1">
                        <li>
                          <Link
                            to="/customers"
                            data-testid="nav-customers"
                            className={`sidebar-item ${location.pathname === '/customers' ? 'active' : ''}`}
                          >
                            <Users className="w-4 h-4" />
                            <span>Clientes</span>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/customer-account"
                            data-testid="nav-customer-account"
                            className={`sidebar-item ${location.pathname === '/customer-account' ? 'active' : ''}`}
                          >
                            <FileBox className="w-4 h-4" />
                            <span>Conta Corrente</span>
                          </Link>
                        </li>
                      </ul>
                    )}
                  </li>
                )}

                {(() => {
                  const item = navItems.find((entry) => entry.path === '/notificationsbooking');
                  if (!item || !canAccess(item.path)) return null;
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <li className="pt-2 border-t border-slate-800/70">
                      <Link
                        to={item.path}
                        data-testid="nav-notificationsbooking"
                        className={`sidebar-item ${isActive ? 'active' : ''}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })()}
              </ul>
            </nav>
          {/* User Info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => canUseGlobalView && setShowGlobalViewModal(true)}
                className="flex flex-1 min-w-0 items-center gap-3 rounded-xl p-1.5 text-left hover:bg-slate-800 transition-colors"
                title={canUseGlobalView ? (isGlobalView ? 'Gerir vista Global' : 'Abrir vista Global') : 'Perfil do utilizador'}
              >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isGlobalView ? 'bg-amber-500/20 ring-1 ring-amber-400/50' : 'bg-slate-700'}`}>
                <User className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className={`text-xs ${isGlobalView ? 'text-amber-400' : 'text-slate-500'}`}>
                  {isGlobalView ? 'Vista Global ativa' : 'Vista normal'}
                </p>
              </div>
              </button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-slate-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main className={`main-content ${location.pathname === '/supplier-replenishment' ? 'min-w-0' : ''} ${(hideChromeLayout || (sidebarOpen === false)) ? 'main-content-expanded' : ''}`}>
          <WorkdayConfirmDialog />
          <UserSecurityModal
            open={showUserSecurityModal}
            onOpenChange={setShowUserSecurityModal}
            user={user}
          />

          {showGlobalViewModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onMouseDown={() => setShowGlobalViewModal(false)}>
              <div className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-950 p-6 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
                <div className="flex items-center gap-3">
                  <div className={`rounded-xl p-3 ${isGlobalView ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                    {isGlobalView ? <ShieldCheck className="h-6 w-6" /> : <LockKeyhole className="h-6 w-6" />}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{isGlobalView ? 'Vista Global ativa' : 'Abrir vista Global'}</h2>
                    <p className="text-sm text-slate-400">Área financeira e de administração protegida.</p>
                  </div>
                </div>

                {isGlobalView ? (
                  <div className="mt-6 space-y-3">
                    <p className="text-sm text-slate-300">Os totais, relatórios, fechos e configurações estão visíveis neste computador.</p>
                    <Button className="w-full bg-slate-800 hover:bg-slate-700" onClick={() => { lockGlobalView(); setShowGlobalViewModal(false); navigate('/'); }}>
                      <LockKeyhole className="mr-2 h-4 w-4" /> Voltar à vista normal
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleGlobalUnlock} className="mt-6 space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-200">Código de acesso</label>
                      <input
                        autoFocus
                        type="password"
                        inputMode="numeric"
                        maxLength={12}
                        value={globalPin}
                        onChange={(event) => { setGlobalPin(event.target.value); setGlobalPinError(''); }}
                        className="h-12 w-full rounded-xl border border-slate-700 bg-slate-900 px-4 text-center text-xl tracking-[0.35em] text-white outline-none focus:border-emerald-500"
                        placeholder="••••"
                      />
                      {globalPinError && <p className="mt-2 text-sm text-rose-400">{globalPinError}</p>}
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setShowGlobalViewModal(false)}>Cancelar</Button>
                      <Button type="submit" className="flex-1 bg-emerald-500 text-slate-950 hover:bg-emerald-400" disabled={!globalPin || globalPinLoading}>
                        {globalPinLoading ? 'A validar…' : 'Abrir Global'}
                      </Button>
                    </div>
                    <p className="text-center text-xs text-slate-500">A proteção fecha automaticamente após 30 minutos ou ao terminar sessão.</p>
                  </form>
                )}
              </div>
            </div>
          )}

          {showInternalChat && !hideChromeLayout && (
            <div
              ref={chatFloatingRef}
              className="fixed z-50 bg-slate-950 border border-slate-800 shadow-2xl shadow-black/50 rounded-2xl overflow-hidden flex flex-col"
              style={getChatFloatingStyle()}
              data-testid="internal-chat-floating-panel"
            >
              <div
                className="h-14 px-4 border-b border-slate-800 flex items-center justify-between cursor-move select-none bg-slate-950/95"
                onMouseDown={startChatDrag}
                onTouchStart={startChatDrag}
                title="Arrastar chat"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                  <Move className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white font-semibold leading-tight truncate">Chat interno</p>
                    <p className="text-xs text-slate-400 truncate">
                      Ligado como {user?.name || user?.email || 'utilizador'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={resetChatFloatingPosition}
                    className="h-8 px-2 text-xs text-slate-400 hover:text-white"
                    title="Repor posição do chat"
                  >
                    Repor
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={() => setShowInternalChat(false)}
                    className="text-slate-400 hover:text-white"
                    title="Fechar chat"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[280px_1fr]">
                <aside className="border-r border-slate-800 min-h-0 overflow-y-auto">
                  <div className="p-3 border-b border-slate-800">
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Utilizadores</p>
                    <p className="text-[11px] text-slate-600 mt-1">Verde = online agora</p>
                  </div>

                  {orderedChatUsers.length === 0 ? (
                    <div className="p-4 text-sm text-slate-400">Não há outros utilizadores ativos para conversar.</div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {orderedChatUsers.map((chatUser) => {
                        const thread = chatThreadForUser(chatUser);
                        const unread = Number(thread?.unread_count || 0);
                        const active = String(selectedChatUser?.id || '') === String(chatUser.id);

                        return (
                          <button
                            key={chatUser.id}
                            type="button"
                            onClick={() => openChatWithUser(chatUser)}
                            className={`w-full text-left rounded-xl px-3 py-3 border transition ${
                              active
                                ? 'bg-emerald-500/15 border-emerald-500/40'
                                : 'bg-slate-900/40 border-slate-800 hover:bg-slate-900'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${chatUser.is_online ? 'bg-emerald-400' : 'bg-slate-600'}`}
                                    title={chatUser.is_online ? 'Online' : 'Offline'}
                                  />
                                  <p className="text-sm font-semibold text-white truncate">{chatUser.name}</p>
                                </div>
                                <p className="text-xs text-slate-500 truncate">
                                  {chatUser.is_online ? 'Online' : 'Offline'} · {chatUser.role || chatUser.email}
                                </p>
                                {thread?.last_message && (
                                  <p className="text-xs text-slate-400 truncate mt-1">{thread.last_message}</p>
                                )}
                              </div>
                              {unread > 0 && (
                                <span className="min-w-5 h-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center px-1">
                                  {unread > 9 ? '9+' : unread}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </aside>

                <section className="min-h-0 flex flex-col">
                  {selectedChatUser ? (
                    <>
                      <div className="h-14 px-5 border-b border-slate-800 flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-white font-semibold truncate">{selectedChatUser.name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {selectedChatUser.is_online ? 'Online agora' : 'Offline'} · {selectedChatUser.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
                        {chatMessages.length === 0 ? (
                          <div className="h-full flex items-center justify-center text-center text-slate-500 text-sm">
                            Ainda não há mensagens nesta conversa.
                          </div>
                        ) : (
                          chatMessages.map((msg) => {
                            const mine = String(msg.sender_id) === String(user?.id);
                            return (
                              <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                <div
                                  className={`max-w-[78%] rounded-2xl px-4 py-2 shadow-sm ${
                                    mine
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-slate-800 text-slate-100'
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                                  <p className={`text-[10px] mt-1 ${mine ? 'text-emerald-100' : 'text-slate-400'}`}>
                                    {formatChatTime(msg.created_at)}
                                  </p>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={chatBottomRef} />
                      </div>

                      {chatError && (
                        <div className="px-5 py-2 text-sm text-rose-300 bg-rose-950/30 border-t border-rose-900/40">
                          {chatError}
                        </div>
                      )}

                      <div className="p-4 border-t border-slate-800 flex items-end gap-2">
                        <textarea
                          value={chatText}
                          onChange={(e) => setChatText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendChatMessage();
                            }
                          }}
                          maxLength={1000}
                          rows={2}
                          placeholder="Escrever mensagem..."
                          className="flex-1 resize-none rounded-xl bg-slate-900 border border-slate-700 text-white placeholder:text-slate-500 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <Button
                          onClick={sendChatMessage}
                          disabled={chatLoading || !chatText.trim()}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Enviar
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-center p-8">
                      <div>
                        <MessageCircle className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                        <p className="text-white font-semibold">Escolhe um utilizador</p>
                        <p className="text-sm text-slate-500 mt-1">A conversa aparece aqui.</p>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          )}
        {/* Top Bar */}
        {!hideChromeLayout && (
          <header className="h-16 bg-slate-900/50 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-30">
            <div className="h-full px-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden"
                >
                  {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
                <h2 className="text-lg font-semibold text-white">
                  {navItems.find(item => item.path === location.pathname)?.label || brandName}
                </h2>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  onClick={() => {
                    enableChatSound();
                    stopInternalChatAlertRepeat();
                    setShowInternalChat(true);
                    refreshChatSummary();
                  }}
                  title="Chat interno"
                >
                  <MessageCircle className="w-5 h-5 text-slate-400" />
                  {chatUnreadCount > 0 && (
                    <>
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-rose-500/40 animate-ping" />
                      <span className="absolute -top-2 -right-2 min-w-7 h-7 px-2 rounded-full bg-rose-600 text-white text-xs font-bold flex items-center justify-center shadow-lg shadow-rose-500/50 ring-2 ring-white animate-pulse">
                        {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                      </span>
                    </>
                  )}
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative"
                      title={(smsUnreadCount + orderAlerts.length) > 0 ? `${smsUnreadCount + orderAlerts.length} alertas por ler` : 'Sem alertas por ler'}
                      onClick={() => { refreshSmsAlerts(); refreshOrderAlerts(); }}
                    >
                      <Bell className={`w-5 h-5 ${(smsUnreadCount + orderAlerts.length) > 0 ? 'text-rose-300' : 'text-slate-400'}`} />
                      {(smsUnreadCount + orderAlerts.length) > 0 && (
                        <>
                          <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-rose-500/40 animate-ping" />
                          <span className="absolute -top-1 -right-1 min-w-6 h-6 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center shadow-lg shadow-rose-500/40 ring-2 ring-slate-950">
                            {(smsUnreadCount + orderAlerts.length) > 99 ? '99+' : (smsUnreadCount + orderAlerts.length)}
                          </span>
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-80 bg-slate-950 border-slate-800 text-slate-100">
                    <div className="px-3 py-2 border-b border-slate-800">
                      <div className="font-semibold text-sm flex items-center justify-between gap-2">
                        <span>Alertas</span>
                        {smsAlertLoading && <span className="text-xs text-slate-500">A atualizar...</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {(smsUnreadCount + orderAlerts.length) > 0
                          ? `${smsUnreadCount + orderAlerts.length} alerta${(smsUnreadCount + orderAlerts.length) === 1 ? '' : 's'} por visualizar`
                          : 'Não há alertas por visualizar'}
                      </div>
                    </div>

                    {orderAlerts.length > 0 && (
                      <div className="max-h-64 overflow-y-auto py-1 border-b border-slate-800">
                        <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-amber-400">Encomendas atualizadas</div>
                        {orderAlerts.map((order) => (
                          <DropdownMenuItem key={order.id} onClick={() => navigate('/customer-orders')} className="cursor-pointer focus:bg-slate-900">
                            <div className="min-w-0 border-l-2 border-amber-400 pl-2">
                              <div className="text-sm font-medium text-slate-100 truncate">{order.order_number} · {order.customer_name}</div>
                              <div className="text-xs text-amber-300 mt-0.5">
                                {order.pickup_overdue
                                  ? `Aguarda levantamento há ${order.pickup_wait_days || 0} dias`
                                  : order.status === 'notified' ? 'Produto chegou e cliente notificado' : 'Produto chegou — notificação requer atenção'}
                              </div>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    )}

                    {smsUnreadPreview.length > 0 ? (
                      <div className="max-h-72 overflow-y-auto py-1">
                        {smsUnreadPreview.map((sms) => (
                          <DropdownMenuItem
                            key={sms.id}
                            onClick={() => navigate('/communications-sms')}
                            className="cursor-pointer focus:bg-slate-900"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-100 truncate">
                                {sms.phone_number || 'Número desconhecido'}
                              </div>
                              <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">
                                {sms.message_text || 'SMS recebida'}
                              </div>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ) : (
                      orderAlerts.length === 0 && <div className="px-3 py-4 text-sm text-slate-500">
                        Tudo limpo por agora.
                      </div>
                    )}

                    <DropdownMenuSeparator className="bg-slate-800" />

                    <DropdownMenuItem
                      onClick={() => navigate('/communications-sms')}
                      className="cursor-pointer focus:bg-slate-900"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Abrir Comunicações SMS
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-400" />
                      </div>
                      <span className="text-sm text-slate-300 hidden sm:inline">{user?.name}</span>
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {user?.role === 'admin' && isGlobalView && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/settings')}>
                          <Settings className="w-4 h-4 mr-2" />
                          Configurações
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    <DropdownMenuItem onClick={() => setShowUserSecurityModal(true)}>
                      <Settings className="w-4 h-4 mr-2" />
                      Segurança da conta
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-rose-400">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>
        )}

        {/* Page Content */}
        <div className={isPOS ? '' : 'p-6'}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
